import { HiPencil } from 'react-icons/hi2';
import { useDisclosure } from '@mantine/hooks';
import {
  Modal,
  UnstyledButton,
  TextInput,
  Group,
  Button,
  NumberInput,
  Select,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import useEmpTimeMapping from '@/hooks/employee/useEmpTimeMapping';
import useEmpTypeMapping from '@/hooks/employee/useEmpTypeMapping';
import useEmployee from '@/hooks/employee/useEmployee';
import useSyncFormWithEmployee from '@/hooks/employee/useSyncFormWithEmployee';
import useUpdateEmployee from '@/hooks/employee/useUpdateEmployee';
import { type UpdateEmployee } from '@/schemas/API/PUT/employee/request';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { isEmptyForm } from '@/utils/functions';
import { goodNotification } from '@/components/Notification/Notification';

export const EmploymentContractCard: React.FC = () => {
  const { employee, error } = useEmployee();
  const { updateEmployee } = useUpdateEmployee();

  const form = useForm<UpdateEmployee>({});

  useSyncFormWithEmployee(employee, form);

  const [opened, { open, close }] = useDisclosure(false);

  const empTypeMap = useEmpTypeMapping();
  const empTimeMap = useEmpTimeMapping();

  if (error) return <SectionErrorNotification />;
  if (!employee || isEmptyForm(form) || !empTypeMap || !empTimeMap)
    return <SectionLoadingOverlay />;

  const handleSave = () => {
    if (form.validate().hasErrors) {
      console.error('Form has errors:', form.validate().errors);
      return;
    }
    void updateEmployee(employee.id, form.values);
    goodNotification('Změny byly uloženy');
    close();
  };

  return (
    <>
      <Modal opened={opened} onClose={close} centered title="Pracovní smlouva">
        <Select
          variant="filled"
          radius="md"
          label="Typ úvazku"
          allowDeselect={false}
          data={MappingToComboBoxItem(empTimeMap)}
          {...form.getInputProps('empTime')}
        />
        <TextInput
          variant="filled"
          radius="md"
          placeholder="optimum"
          label="Optimum"
          value={employee.optimum}
          readOnly
        />
        <NumberInput
          variant="filled"
          radius="md"
          placeholder="počet dní dovolené"
          label="Počet dní dovolené"
          min={0}
          {...form.getInputProps('numOfVacationDays')}
        />

        <Group justify="space-between" grow align="start" className="mt-4">
          <Button variant="outline" onClick={close}>
            Zrušit
          </Button>
          <Button variant="filled" onClick={handleSave}>
            Uložit
          </Button>
        </Group>
      </Modal>

      <UnstyledButton onClick={open}>
        <div className="flex flex-row items-center justify-between p-2.5 border rounded-lg border-card">
          <h1 className="font-semibold">Pracovní smlouva</h1>
          <HiPencil size="1.5rem" className="shrink-0" />
        </div>
      </UnstyledButton>
    </>
  );
};
